import { ArrowLeft, ChevronDown, ExternalLink, Hash, Search, ShieldCheck, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import bchannelLogo from "../../../../logo.png";
import type { ComposerDraft } from "../../../shared/types";
import { type AdminMessage, type AdminPerson, api } from "../../api";
import { Avatar, LoadingRaccoon } from "../../components/primitives";
import { MessagePreview } from "../composer/MessagePreview";

function shortDate(value: string | null) {
  if (!value) return "Never";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function JsonRecord({ value }: { value: unknown }) {
  return (
    <details className="admin-raw">
      <summary>
        <ChevronDown size={13} /> Raw data
      </summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

function AdminPersonRow({
  person,
  selected,
  changing,
  onSelect,
  onBan,
}: {
  person: AdminPerson;
  selected: boolean;
  changing: boolean;
  onSelect: () => void;
  onBan: () => void;
}) {
  return (
    <article className={`admin-person ${selected ? "selected" : ""}`}>
      <button type="button" className="admin-person-main" onClick={onSelect}>
        <Avatar name={person.displayName} src={person.avatarUrl} size={38} />
        <span>
          <strong>{person.displayName}</strong>
          <small>
            @{person.username} · {person.slackUserId}
          </small>
          {person.realName && person.realName !== person.displayName && <small>{person.realName}</small>}
        </span>
      </button>
      <div className="admin-person-meta">
        <span>{person.messageCount} messages</span>
        {person.isOwner && <b>Owner</b>}
        {!person.isOwner && person.isAdmin && <b>Admin</b>}
        {person.banned && <b className="is-banned">Banned</b>}
        <button
          type="button"
          className={person.banned ? "unban" : "ban"}
          disabled={changing || person.slackUserId === "U078PH0GBEH"}
          onClick={onBan}
        >
          {changing ? <LoadingRaccoon size={15} /> : person.banned ? "Unban" : "Ban"}
        </button>
      </div>
      {selected && (
        <div className="admin-person-detail">
          <dl>
            <div>
              <dt>Last message</dt>
              <dd>{shortDate(person.lastMessageAt)}</dd>
            </div>
            <div>
              <dt>Internal ID</dt>
              <dd>{person.internalId ?? "Not stored yet"}</dd>
            </div>
          </dl>
          <JsonRecord value={person.raw} />
        </div>
      )}
    </article>
  );
}

function previewDraft(message: AdminMessage): ComposerDraft {
  return {
    id: message.draftId,
    title: message.title,
    status: message.slackMessageTs ? "sent" : message.status === "failed" ? "failed" : "draft",
    ping: message.ping,
    version: 1,
    document: message.document,
    sender: {
      id: message.sender.slackUserId,
      name: message.senderName,
      username: message.sender.username,
      displayName: message.sender.displayName,
      realName: message.sender.realName,
      selectedName: message.senderName,
      nameMode: "display",
      avatarUrl: message.sender.avatarUrl,
    },
    channel: message.channel,
    createdAt: message.createdAt,
    updatedAt: message.updatedAt,
    publishedMessageId: message.publishedMessageId,
  };
}

function AdminMessageRow({ message }: { message: AdminMessage }) {
  const [open, setOpen] = useState(false);
  const slackUrl = message.slackMessageTs
    ? `https://hackclub.slack.com/archives/${message.channel.id}/p${message.slackMessageTs.replace(".", "")}`
    : null;
  return (
    <article className={`admin-message ${open ? "open" : ""}`}>
      <button type="button" className="admin-message-head" onClick={() => setOpen((current) => !current)}>
        <Avatar name={message.sender.displayName} src={message.sender.avatarUrl} size={34} />
        <span className="admin-message-copy">
          <strong>{message.title}</strong>
          <small>
            {message.sender.displayName} · #{message.channel.name} · {shortDate(message.sentAt ?? message.updatedAt)}
          </small>
          <span>{message.fallbackText || "No fallback text"}</span>
        </span>
        <span className={`admin-status ${message.status}`}>{message.status}</span>
        <ChevronDown size={15} />
      </button>
      {open && (
        <div className="admin-message-detail">
          <dl className="admin-message-ids">
            <div>
              <dt>Draft</dt>
              <dd>{message.draftId}</dd>
            </div>
            <div>
              <dt>Message</dt>
              <dd>{message.publishedMessageId ?? "Not published"}</dd>
            </div>
            <div>
              <dt>Slack timestamp</dt>
              <dd>{message.slackMessageTs ?? "—"}</dd>
            </div>
          </dl>
          {slackUrl && (
            <a className="admin-slack-link" href={slackUrl} target="_blank" rel="noreferrer">
              Open in Slack <ExternalLink size={13} />
            </a>
          )}
          <div className="admin-preview">
            <MessagePreview draft={previewDraft(message)} document={message.document} embedded />
          </div>
          <JsonRecord value={message.raw} />
        </div>
      )}
    </article>
  );
}

export function AdminPage() {
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState<AdminPerson[]>([]);
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [changing, setChanging] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      setLoadingPeople(true);
      api
        .adminPeople(query)
        .then((result) => active && setPeople(result))
        .catch((cause) => active && setError(cause instanceof Error ? cause.message : "Could not search people."))
        .finally(() => active && setLoadingPeople(false));
    }, 180);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      setLoadingMessages(true);
      api
        .adminMessages(query, selectedUser)
        .then((result) => active && setMessages(result))
        .catch((cause) => active && setError(cause instanceof Error ? cause.message : "Could not search messages."))
        .finally(() => active && setLoadingMessages(false));
    }, 180);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query, selectedUser]);

  const selectedPerson = useMemo(
    () => people.find((person) => person.slackUserId === selectedUser),
    [people, selectedUser],
  );

  const toggleBan = async (person: AdminPerson) => {
    if (changing) return;
    setChanging(person.slackUserId);
    setError("");
    try {
      const result = await api.setAdminBan(person.slackUserId, !person.banned);
      setPeople((current) =>
        current.map((item) => (item.slackUserId === person.slackUserId ? { ...item, banned: result.banned } : item)),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not update this ban.");
    } finally {
      setChanging("");
    }
  };

  if (error && error === "This page is private.")
    return (
      <main className="admin-denied">
        <ShieldCheck size={22} />
        <strong>Private page</strong>
        <a href="/">Back to bChannel</a>
      </main>
    );

  return (
    <main className="admin-page">
      <header className="admin-header">
        <a href="/" className="admin-back" aria-label="Back to bChannel">
          <img src={bchannelLogo} alt="" />
          <ArrowLeft size={15} />
        </a>
        <strong>Admin</strong>
      </header>
      <div className="admin-search">
        <Search size={17} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search names, text, IDs, Slack timestamps, channels, or raw message data"
        />
        {(loadingPeople || loadingMessages) && <LoadingRaccoon size={22} />}
      </div>
      {error && <p className="admin-error">{error}</p>}
      <div className="admin-layout">
        <aside className="admin-people-pane">
          <div className="admin-section-title">
            <UserRound size={14} />
            <strong>People</strong>
            <span>{people.length}</span>
          </div>
          <div className="admin-people-list">
            {people.map((person) => (
              <AdminPersonRow
                key={person.slackUserId}
                person={person}
                selected={selectedUser === person.slackUserId}
                changing={changing === person.slackUserId}
                onSelect={() =>
                  setSelectedUser((current) => (current === person.slackUserId ? "" : person.slackUserId))
                }
                onBan={() => void toggleBan(person)}
              />
            ))}
            {!loadingPeople && !people.length && <p className="admin-empty">No people found.</p>}
          </div>
        </aside>
        <section className="admin-messages-pane">
          <div className="admin-section-title">
            <Hash size={14} />
            <strong>{selectedPerson ? `${selectedPerson.displayName}’s messages` : "Messages"}</strong>
            <span>{messages.length}</span>
            {selectedUser && (
              <button type="button" onClick={() => setSelectedUser("")}>
                Show everyone
              </button>
            )}
          </div>
          <div className="admin-message-list">
            {messages.map((message) => (
              <AdminMessageRow key={message.id} message={message} />
            ))}
            {!loadingMessages && !messages.length && <p className="admin-empty">No messages found.</p>}
          </div>
        </section>
      </div>
    </main>
  );
}
